import React, { useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Text,
  Grid,
  VStack,
  HStack,
  Image,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);

interface PassportModalProps {
  isOpen: boolean;
  onClose: () => void;
  visitedEvents: Array<{
    EventoId?: number;
    Titolo: string;
    DataEvento: string;
  }>;
  allEvents: Array<{
    EventoId?: number;
    Titolo: string;
    DataEvento: string;
  }>;
}

interface Country {
  countryName: string;
  countryCode: string;
  iso2: string;
  visited: boolean;
  visitDate?: string;
  eventTitle?: string;
}

const PassportModal: React.FC<PassportModalProps> = ({ isOpen, onClose, visitedEvents, allEvents }) => {
  
  // Country mapping with ISO alpha-2 codes for flag images
  const countryMapping: { [key: string]: { name: string; code: string; iso2: string } } = {
    'thailandia': { name: 'Thailandia', code: 'THA', iso2: 'th' },
    'thailand': { name: 'Thailandia', code: 'THA', iso2: 'th' },
    'turchia': { name: 'Turchia', code: 'TUR', iso2: 'tr' },
    'turkey': { name: 'Turchia', code: 'TUR', iso2: 'tr' },
    'cuba': { name: 'Cuba', code: 'CUB', iso2: 'cu' },
    'messico': { name: 'Messico', code: 'MEX', iso2: 'mx' },
    'mexico': { name: 'Messico', code: 'MEX', iso2: 'mx' },
    'marocco': { name: 'Marocco', code: 'MAR', iso2: 'ma' },
    'morocco': { name: 'Marocco', code: 'MAR', iso2: 'ma' },
    'mozambico': { name: 'Mozambico', code: 'MOZ', iso2: 'mz' },
    'mozambique': { name: 'Mozambico', code: 'MOZ', iso2: 'mz' },
    'india': { name: 'India', code: 'IND', iso2: 'in' },
    'kerala': { name: 'India', code: 'IND', iso2: 'in' },
    'portogallo': { name: 'Portogallo', code: 'PRT', iso2: 'pt' },
    'portugal': { name: 'Portogallo', code: 'PRT', iso2: 'pt' },
    'giappone': { name: 'Giappone', code: 'JPN', iso2: 'jp' },
    'japan': { name: 'Giappone', code: 'JPN', iso2: 'jp' },
    'cina': { name: 'Cina', code: 'CHN', iso2: 'cn' },
    'china': { name: 'Cina', code: 'CHN', iso2: 'cn' },
    'francia': { name: 'Francia', code: 'FRA', iso2: 'fr' },
    'france': { name: 'Francia', code: 'FRA', iso2: 'fr' },
    'spagna': { name: 'Spagna', code: 'ESP', iso2: 'es' },
    'spain': { name: 'Spagna', code: 'ESP', iso2: 'es' },
    'grecia': { name: 'Grecia', code: 'GRC', iso2: 'gr' },
    'greece': { name: 'Grecia', code: 'GRC', iso2: 'gr' },
    'etiopia': { name: 'Etiopia', code: 'ETH', iso2: 'et' },
    'ethiopia': { name: 'Etiopia', code: 'ETH', iso2: 'et' },
    'peru': { name: 'Perù', code: 'PER', iso2: 'pe' },
    'perù': { name: 'Perù', code: 'PER', iso2: 'pe' },
    'brasil': { name: 'Brasile', code: 'BRA', iso2: 'br' },
    'brazil': { name: 'Brasile', code: 'BRA', iso2: 'br' },
    'argentina': { name: 'Argentina', code: 'ARG', iso2: 'ar' },
    'vietnam': { name: 'Vietnam', code: 'VNM', iso2: 'vn' },
    'corea': { name: 'Corea del Sud', code: 'KOR', iso2: 'kr' },
    'korea': { name: 'Corea del Sud', code: 'KOR', iso2: 'kr' },
    'libano': { name: 'Libano', code: 'LBN', iso2: 'lb' },
    'lebanon': { name: 'Libano', code: 'LBN', iso2: 'lb' },
    'indonesia': { name: 'Indonesia', code: 'IDN', iso2: 'id' },
    'australia': { name: 'Australia', code: 'AUS', iso2: 'au' },
    'usa': { name: 'Stati Uniti', code: 'USA', iso2: 'us' },
    'stati uniti': { name: 'Stati Uniti', code: 'USA', iso2: 'us' },
    'germania': { name: 'Germania', code: 'DEU', iso2: 'de' },
    'germany': { name: 'Germania', code: 'DEU', iso2: 'de' },
    'inghilterra': { name: 'Regno Unito', code: 'GBR', iso2: 'gb' },
    'england': { name: 'Regno Unito', code: 'GBR', iso2: 'gb' },
    'gran bretagna': { name: 'Regno Unito', code: 'GBR', iso2: 'gb' },
    'regno unito': { name: 'Regno Unito', code: 'GBR', iso2: 'gb' },
    'uk': { name: 'Regno Unito', code: 'GBR', iso2: 'gb' },
    'irlanda': { name: 'Irlanda', code: 'IRL', iso2: 'ie' },
    'ireland': { name: 'Irlanda', code: 'IRL', iso2: 'ie' },
    'filippine': { name: 'Filippine', code: 'PHL', iso2: 'ph' },
    'philippines': { name: 'Filippine', code: 'PHL', iso2: 'ph' },
    'egitto': { name: 'Egitto', code: 'EGY', iso2: 'eg' },
    'egypt': { name: 'Egitto', code: 'EGY', iso2: 'eg' },
    'sudafrica': { name: 'Sudafrica', code: 'ZAF', iso2: 'za' },
    'south africa': { name: 'Sudafrica', code: 'ZAF', iso2: 'za' },
    'kenya': { name: 'Kenya', code: 'KEN', iso2: 'ke' },
    'canada': { name: 'Canada', code: 'CAN', iso2: 'ca' },
    'colombia': { name: 'Colombia', code: 'COL', iso2: 'co' },
    'cile': { name: 'Cile', code: 'CHL', iso2: 'cl' },
    'chile': { name: 'Cile', code: 'CHL', iso2: 'cl' },
    'croazia': { name: 'Croazia', code: 'HRV', iso2: 'hr' },
    'croatia': { name: 'Croazia', code: 'HRV', iso2: 'hr' },
    'mar baltico': { name: 'Mar Baltico', code: 'FIN', iso2: 'fi' },
    'baltico': { name: 'Mar Baltico', code: 'FIN', iso2: 'fi' },
    'finlandia': { name: 'Finlandia', code: 'FIN', iso2: 'fi' },
    'finland': { name: 'Finlandia', code: 'FIN', iso2: 'fi' },
    'lituania': { name: 'Lituania', code: 'LTU', iso2: 'lt' },
    'lithuania': { name: 'Lituania', code: 'LTU', iso2: 'lt' },
    'lettonia': { name: 'Lettonia', code: 'LVA', iso2: 'lv' },
    'latvia': { name: 'Lettonia', code: 'LVA', iso2: 'lv' },
    'estonia': { name: 'Estonia', code: 'EST', iso2: 'ee' },
  };

  const getCountryFromTitle = (title: string): { name: string; code: string; iso2: string } | null => {
    const t = title.toLowerCase();
    for (const [key, value] of Object.entries(countryMapping)) {
      if (t.includes(key)) {
        return value;
      }
    }
    return null;
  };

  const countries = useMemo(() => {
    // Get all unique countries from all events
    const allCountriesMap = new Map<string, Country>();
    
    allEvents.forEach(event => {
      const country = getCountryFromTitle(event.Titolo);
      if (country && !allCountriesMap.has(country.code)) {
        allCountriesMap.set(country.code, {
          countryName: country.name,
          countryCode: country.code,
          iso2: country.iso2,
          visited: false,
        });
      }
    });
    
    // Mark visited countries
    visitedEvents.forEach(event => {
      const country = getCountryFromTitle(event.Titolo);
      if (country && allCountriesMap.has(country.code)) {
        const existing = allCountriesMap.get(country.code)!;
        if (!existing.visited) {
          allCountriesMap.set(country.code, {
            ...existing,
            visited: true,
            visitDate: event.DataEvento,
            eventTitle: event.Titolo,
          });
        }
      }
    });
    
    return Array.from(allCountriesMap.values()).sort((a, b) => 
      a.countryName.localeCompare(b.countryName)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allEvents, visitedEvents]);

  const stats = useMemo(() => {
    const visitedCount = countries.filter(c => c.visited).length;
    return {
      visited: visitedCount,
      total: countries.length,
    };
  }, [countries]);

  // Calculate optimal columns to distribute evenly across rows
  const gridCols = useMemo(() => {
    const count = countries.length;
    if (count <= 3) return { base: 2, sm: count, md: count, lg: count };
    const lgCols = Math.min(Math.ceil(count / 2), 5);
    const mdCols = Math.min(lgCols, 4);
    const smCols = Math.min(mdCols, 3);
    return { base: 2, sm: smCols, md: mdCols, lg: lgCols };
  }, [countries.length]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.900" backdropFilter="blur(10px)" />
      <ModalContent 
        bg="gray.900"
        border="1px solid"
        borderColor="whiteAlpha.200"
        maxH="90vh"
      >
        <ModalHeader 
          borderBottom="1px solid"
          borderColor="whiteAlpha.200"
          py={6}
        >
          <VStack align="stretch" spacing={3}>
            <Text
              fontSize="2xl"
              color="white"
              textTransform="uppercase"
              letterSpacing="widest"
              fontWeight="400"
            >
              Il Mio Passaporto
            </Text>
            
            <HStack spacing={8} fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide">
              <Text>
                <Text as="span" color="white" fontWeight="500">{stats.visited}</Text> / {stats.total} Paesi Visitati
              </Text>
            </HStack>
            {stats.visited === 0 && (
              <Text color="gray.400" fontSize="xs" fontStyle="italic">
                Nessun evento visitato ancora
              </Text>
            )}
          </VStack>
        </ModalHeader>
        
        <ModalCloseButton color="white" />
        
        <ModalBody py={8} px={6}>
          {countries.length === 0 ? (
            <VStack spacing={4} py={12}>
              <Text fontSize="4xl">🌍</Text>
              <Text color="gray.500" textAlign="center" textTransform="uppercase" letterSpacing="wide" fontSize="xs">
                Nessun paese ancora
              </Text>
            </VStack>
          ) : (
            <Grid 
              templateColumns={{
                base: `repeat(${gridCols.base}, 1fr)`,
                sm: `repeat(${gridCols.sm}, 1fr)`,
                md: `repeat(${gridCols.md}, 1fr)`,
                lg: `repeat(${gridCols.lg}, 1fr)`,
              }}
              gap={4}
            >
              {countries.map((country, index) => (
                <MotionBox
                  key={country.countryCode}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.02 }}
                >
                  <Box
                    position="relative"
                    bg={country.visited ? "whiteAlpha.100" : "whiteAlpha.50"}
                    border="1px solid"
                    borderColor={country.visited ? "whiteAlpha.300" : "whiteAlpha.100"}
                    p={4}
                    h="140px"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    _hover={{
                      borderColor: country.visited ? "white" : "whiteAlpha.200",
                      bg: country.visited ? "whiteAlpha.150" : "whiteAlpha.100",
                    }}
                    transition="all 0.2s"
                    cursor="pointer"
                    opacity={country.visited ? 1 : 0.4}
                  >
                    {/* Stamp icon for visited */}
                    {country.visited && (
                      <Box
                        position="absolute"
                        top={2}
                        right={2}
                        color="white"
                        fontSize="xs"
                      >
                        ✓
                      </Box>
                    )}
                    
                    <VStack spacing={2}>
                      <HStack spacing={2} alignItems="center">
                        <Image
                          src={`https://flagcdn.com/w40/${country.iso2}.png`}
                          alt={country.countryName}
                          w="20px"
                          h="15px"
                          objectFit="cover"
                          borderRadius="2px"
                          fallback={<Box w="20px" h="15px" bg="gray.600" borderRadius="2px" />}
                        />
                        <Text 
                          color={country.visited ? "white" : "gray.600"}
                          fontSize="xs"
                          textTransform="uppercase"
                          letterSpacing="widest"
                          textAlign="center"
                          fontWeight={country.visited ? "500" : "400"}
                          noOfLines={2}
                          minH="28px"
                          display="flex"
                          alignItems="center"
                        >
                          {country.countryName}
                        </Text>
                      </HStack>
                      
                      {country.visited && country.visitDate && (
                        <Text 
                          color="gray.400" 
                          fontSize="xs"
                          letterSpacing="wide"
                          fontFamily="monospace"
                        >
                          {formatDate(country.visitDate)}
                        </Text>
                      )}
                    </VStack>
                  </Box>
                </MotionBox>
              ))}
            </Grid>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default PassportModal;
